import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Clock, Paperclip, ChevronRight, PlusCircle,
  Filter, FileCheck2, Send, X, Loader2, ChevronDown, ChevronUp,
  ShieldAlert, Timer,
} from "lucide-react";
import Layout from "@/components/Layout";
import { FORM_TYPE_LABEL } from "@/components/FormDetails";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin, MANUAL_TAT_OPTIONS, tatHoursFor } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";

const FILTERS = [
  ["all", "All"],
  ["material", "Material"],
  ["event", "Event"],
  ["transportation", "Transportation"],
  ["fnb", "F&B"],
  ["hotel", "Hotel"],
  ["travel", "Travel"],
];

/* ------------------- helpers ------------------- */

function linearStatuses(cfg, form_type) {
  const v = cfg?.statuses?.[form_type];
  return Array.isArray(v) ? v : null;
}
function eventTracks(cfg) { return cfg?.statuses?.event?.tracks || []; }
function eventFinal(cfg) { return cfg?.statuses?.event?.final || []; }

function ageWorkingHours(iso) {
  const mins = (new Date() - new Date(iso)) / 60000;
  return (mins / 60).toFixed(1);
}

function currentIndex(list, current) {
  const i = list.indexOf(current);
  return i < 0 ? -1 : i;
}

function nextAllowedLinear(list, current) {
  // Linear forms allow forward-only, but Order Issues / Booking Cancelled
  // are branches from the same start. Simplest UX: allow any not-yet-set
  // status that comes AT or AFTER the current one, plus Requisition Completed
  // only when at Bill Paid (or Bill / Chalan Paid for travel).
  if (!current) return list.slice(0, list.length);
  const i = list.indexOf(current);
  if (i < 0) return list;
  return list.slice(i + 1);
}

/* ------------------- KPIs ------------------- */
function useKpis(rows) {
  return useMemo(() => {
    const inProgress = rows.filter((r) => r.current_stage === "process_owner").length;
    const awaitingBill = rows.filter((r) => (r.process_owner_sub_status || "").toLowerCase().includes("received")).length;
    const today = new Date().toDateString();
    const completedToday = rows.filter((r) => r.status === "completed" && new Date(r.updated_at).toDateString() === today).length;
    // Overdue: process_owner stage, and hours since HOS approval > 5 working days (approx 40h)
    const overdue = rows.filter((r) => {
      if (r.current_stage !== "process_owner") return false;
      const hosAt = (r.audit_trail || []).find((a) => a.stage === "admin2_review" && a.action === "approve")?.at;
      if (!hosAt) return false;
      const h = (Date.now() - new Date(hosAt)) / 3600000;
      return h > 40;
    }).length;
    return { inProgress, awaitingBill, completedToday, overdue };
  }, [rows]);
}

/* ------------------- MAIN PAGE ------------------- */
export default function ProcessOwnerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [pendingRows, setPendingRows] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [historyRows, setHistoryRows] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState({ open: false, req: null, sub: "" });

  const load = () => {
    setLoading(true);
    const superView = isSuperAdmin(user);
    const pendingUrl = superView ? "/requisitions?filter=pending_process_owner" : "/requisitions?filter=pending_process_owner_mine";
    Promise.all([
      api.get(pendingUrl),
      api.get("/requisitions?filter=updated_by_me"),
      api.get("/requisitions?filter=my_assignments_historical"),
      api.get("/fulfilment/config"),
    ]).then(([p, u, h, c]) => {
      setPendingRows(p.data || []);
      setUpdatedRows(u.data || []);
      // Historical: only entries where I'm/was a process owner
      const meAsPO = (h.data || []).filter(
        (r) => superView || (r.process_owner_ids || []).includes(user.id) ||
               (r.fulfilment_history || []).some((f) => f.actor_id === user.id),
      );
      setHistoryRows(meAsPO);
      setCfg(c.data);
    }).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const rows = tab === "pending" ? pendingRows : tab === "updated" ? updatedRows : historyRows;
  const filtered = rows.filter((r) => filter === "all" || r.form_type === filter);
  const kpis = useKpis(pendingRows);

  const applyOptimisticUpdate = (updated) => {
    setPendingRows((cur) => cur.map((r) => r.id === updated.id ? updated : r));
    load();
  };

  return (
    <Layout>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#b48a45] font-semibold">Fulfilment</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight" data-testid="po-heading">
            Process Owner Console
          </h1>
          <div className="mt-1 text-sm text-[#4a524c] flex items-center gap-2 flex-wrap">
            {isSuperAdmin(user) ? (
              <>
                <ShieldAlert size={14} className="text-[#7a1f2b]" />
                <span className="font-medium">Super Admin View — all process owners</span>
                <span className="text-[#6e7770]">· {pendingRows.length} pending, {historyRows.length} historical</span>
              </>
            ) : (
              <span>
                {pendingRows.length} pending · {historyRows.length} historical.
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 min-w-[440px]">
          <KPI label="In Progress"     value={kpis.inProgress}    tone="gold"     testid="po-kpi-inprogress" />
          <KPI label="Awaiting Bill"   value={kpis.awaitingBill}  tone="teal"     testid="po-kpi-awaiting" />
          <KPI label="Completed Today" value={kpis.completedToday} tone="emerald" testid="po-kpi-completed" />
          <KPI label="Overdue"         value={kpis.overdue}       tone="danger"   testid="po-kpi-overdue" />
        </div>
      </div>

      <div className="border-b border-[#e2d7c3] mb-4 flex items-center gap-1 overflow-x-auto" data-testid="po-tabs">
        <PoTabBtn active={tab === "pending"} onClick={() => setTab("pending")}
                  count={pendingRows.length} testid="po-tab-pending">Pending Fulfilment</PoTabBtn>
        <PoTabBtn active={tab === "updated"} onClick={() => setTab("updated")}
                  count={updatedRows.length} testid="po-tab-updated">Updated by Me</PoTabBtn>
        <PoTabBtn active={tab === "history"} onClick={() => setTab("history")}
                  count={historyRows.length} testid="po-tab-history">All My Fulfilment History</PoTabBtn>
      </div>

      <div className="flex items-center gap-1 flex-wrap mb-4" data-testid="po-filters">
        <Filter size={14} className="text-[#6e7770] mr-1" />
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    filter === k ? "bg-[#b48a45] text-white border-[#8a6a2f]" : "bg-white text-[#4a524c] border-[#e2d7c3] hover:bg-[#faf5eb]"
                  }`}
                  data-testid={`po-filter-${k}`}>{l}</button>
        ))}
      </div>

      {loading && <SkeletonList />}
      {!loading && filtered.length === 0 && (
        <div className="sheet p-10 text-center text-[#6e7770]" data-testid="po-empty">
          <FileCheck2 size={28} className="mx-auto mb-2 text-[#b48a45]" />
          <div className="font-serif text-lg text-[#7a1f2b] mb-1">You have no pending items.</div>
          <div>When requisitions arrive, they&apos;ll appear here.</div>
          {isSuperAdmin(user) && (
            <div className="mt-3 text-xs text-[#6e7770]" data-testid="po-empty-super-hint">
              Super Admin can view all queues by toggling the role tabs above.
            </div>
          )}
        </div>
      )}

      <div className="space-y-4">
        {!loading && filtered.map((r) => (
          <FulfilmentCard
            key={r.id}
            req={r}
            cfg={cfg}
            tab={tab}
            expanded={!!expanded[r.id]}
            onToggle={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
            onOpenModal={(sub) => setModal({ open: true, req: r, sub })}
            user={user}
          />
        ))}
      </div>

      <UpdateStatusModal
        open={modal.open}
        req={modal.req}
        initialSub={modal.sub}
        cfg={cfg}
        user={user}
        onClose={() => setModal({ open: false, req: null, sub: "" })}
        onDone={(updated) => { applyOptimisticUpdate(updated); setModal({ open: false, req: null, sub: "" }); }}
      />
    </Layout>
  );
}

function PoTabBtn({ active, onClick, children, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 border-b-2 whitespace-nowrap text-sm font-medium transition-colors ${
        active ? "border-[#b48a45] text-[#b48a45]" :
                 "border-transparent text-[#6e7770] hover:text-[#1f2320]"
      }`}>
      {children}
      {typeof count === "number" && count > 0 && (
        <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
          active ? "bg-[#b48a45] text-white" : "bg-[#e2d7c3] text-[#4a524c]"
        }`}>{count}</span>
      )}
    </button>
  );
}


function KPI({ label, value, tone, testid }) {
  const cls = {
    gold:    "text-[#b48a45] border-l-[#b48a45]",
    teal:    "text-[#2d6b6b] border-l-[#2d6b6b]",
    emerald: "text-emerald-800 border-l-emerald-500",
    danger:  "text-[#b3261e] border-l-[#b3261e]",
  }[tone];
  return (
    <div className={`sheet p-3 border-l-4 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6e7770] font-semibold">{label}</div>
      <div className={`font-serif text-2xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="space-y-3" data-testid="po-skeleton">
      {[1, 2, 3].map((i) => (
        <div key={i} className="sheet p-5 animate-pulse">
          <div className="h-4 w-40 bg-[#f2ecdf] rounded mb-3" />
          <div className="h-3 w-64 bg-[#f2ecdf] rounded mb-2" />
          <div className="h-3 w-full bg-[#f2ecdf] rounded" />
        </div>
      ))}
    </div>
  );
}

/* ------------------- CARD ------------------- */
function FulfilmentCard({ req, cfg, tab, expanded, onToggle, onOpenModal, user }) {
  const isEvent = req.form_type === "event";
  const linear = cfg && !isEvent ? linearStatuses(cfg, req.form_type) : null;
  const current = req.process_owner_sub_status || "";
  const nextOptions = linear ? nextAllowedLinear(linear, current) : [];
  const [nextChoice, setNextChoice] = useState(nextOptions[0] || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNextChoice(nextOptions[0] || ""); }, [current]);
  if (!cfg) return null;
  const readonly = tab && tab !== "pending";

  const followUp = req.next_follow_up_at
    ? new Date(req.next_follow_up_at).toLocaleString()
    : null;
  const assignedNames = (req.process_owner_ids || []).length && req.applicant_name
    ? "" // handled via user list on detail; keep card compact
    : "";

  return (
    <div className="sheet p-5 border-l-4 border-l-[#b48a45] hover-lift" data-testid={`po-card-${req.ref_code}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/requisitions/${req.ref_code}`}
                className="font-mono font-semibold text-[#b48a45] hover:underline">
            {req.ref_code}
          </Link>
          <span className="inline-block px-2 py-0.5 rounded-full bg-[#f9f4e8] text-[#8a6a2f] text-xs font-medium">
            {FORM_TYPE_LABEL[req.form_type] || req.form_type}
          </span>
          {req.is_emergency && <span className="badge bg-[#b3261e] text-white border-[#b3261e]" data-testid={`po-emergency-${req.ref_code}`}>🚨 EMERGENCY</span>}
          {req.late_submission && <span className="badge bg-[#fcebe9] text-[#b3261e] border-[#b3261e]/40">LATE</span>}
          {current && (
            <span className="badge bg-[#e8f2f2] text-[#2d6b6b] border-[#2d6b6b]/40" data-testid={`po-current-${req.ref_code}`}>
              {current}
            </span>
          )}
          {isSuperAdmin(user) && (
            <span className="badge bg-slate-100 text-slate-700 border-slate-300"
                  data-testid={`po-assigned-${req.ref_code}`}>
              Assigned to: {(req.process_owner_ids || []).length} owner{(req.process_owner_ids || []).length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="text-xs text-[#6e7770] flex items-center gap-1">
          <Clock size={12} /> {ageWorkingHours(req.submitted_at || req.created_at)}h ago
        </div>
      </div>

      <div className="mb-1 text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1"
           data-testid={`po-followup-line-${req.ref_code}`}>
        <Timer size={11} className={followUp ? "text-[#b48a45]" : "text-[#6e7770]"} />
        <span className={followUp ? "text-[#b48a45]" : "text-[#6e7770]"}>
          Follow-up: {followUp || "not scheduled"}
        </span>
      </div>

      <div className="text-sm text-[#1f2320] mb-2">
        <span className="font-medium">{req.applicant_name}</span> ·
        <span className="text-[#4a524c] ml-1">{req.form_data?.mainDept} / {req.form_data?.subDept}</span>
      </div>

      {/* Progress stepper */}
      {linear && (
        <div className="mt-2 flex items-center gap-1 overflow-x-auto py-1" data-testid={`po-stepper-${req.ref_code}`}>
          {linear.map((s, i) => {
            const idx = currentIndex(linear, current);
            const state = idx < 0 ? "todo" : i < idx ? "done" : i === idx ? "active" : "todo";
            return (
              <div key={s} className="flex items-center gap-1 flex-shrink-0">
                <span className={`px-2 py-1 rounded-full text-[10px] font-medium border ${
                  state === "done"    ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                  state === "active"  ? "bg-[#b48a45] text-white border-[#8a6a2f]" :
                                        "bg-white text-[#6e7770] border-[#e2d7c3]"
                }`}>{s}</span>
                {i < linear.length - 1 && <ChevronRight size={12} className="text-[#cbbba0]" />}
              </div>
            );
          })}
        </div>
      )}

      {isEvent && !readonly && <EventTracks req={req} cfg={cfg} user={user} onOpenModal={onOpenModal} />}

      {/* Action bar (linear forms only; event forms use the track grid) */}
      {linear && !readonly && req.current_stage === "process_owner" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={nextChoice} onChange={(e) => setNextChoice(e.target.value)}
                  className="input-vgws py-2 max-w-[280px]" data-testid={`po-next-${req.ref_code}`}>
            {nextOptions.length === 0 && <option value="">No further transitions</option>}
            {nextOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <button
            onClick={() => nextChoice && onOpenModal(nextChoice)}
            disabled={!nextChoice}
            className="btn-vgws bg-[#b48a45] border-[#8a6a2f] hover:bg-[#8a6a2f]"
            data-testid={`po-update-${req.ref_code}`}
          >
            <Send size={14} /> Update Status
          </button>
          <button onClick={onToggle} className="link-vgws text-xs inline-flex items-center gap-1 ml-auto"
                  data-testid={`po-expand-${req.ref_code}`}>
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {expanded ? "Hide details" : "Show details"}
          </button>
        </div>
      )}

      {/* Attachments accordion */}
      <FulfilmentAttachments req={req} expanded={expanded} onToggle={onToggle} />
    </div>
  );
}

/* ------------------- EVENT TRACK GRID ------------------- */
function EventTracks({ req, cfg, user, onOpenModal }) {
  const tracks = eventTracks(cfg);
  const finals = eventFinal(cfg);
  const stateMap = req.fulfilment_tracks || {};
  const mine = new Set(cfg.my_event_tracks || []);
  const superUser = isSuperAdmin(user);
  const allDone = tracks.every((t) => (stateMap[t] || {}).status === "done");
  const canFinalize = superUser || (allDone && tracks.every((t) => mine.has(t)));  // Amey.G or Super Admin

  return (
    <div className="mt-3">
      <div className="text-[11px] uppercase tracking-widest text-[#6e7770] font-semibold mb-1">Event tracks</div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid={`po-tracks-${req.ref_code}`}>
        {tracks.map((t) => {
          const s = stateMap[t] || {};
          const done = s.status === "done";
          const canClick = mine.has(t) || superUser;
          return (
            <button
              key={t}
              disabled={done || !canClick}
              onClick={() => onOpenModal(t)}
              className={`flex items-center justify-between text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                done ? "bg-emerald-50 border-emerald-300 text-emerald-800" :
                canClick ? "bg-white border-[#cbbba0] hover:border-[#b48a45] text-[#1f2320]" :
                       "bg-[#faf5eb] border-[#e2d7c3] text-[#6e7770] cursor-not-allowed"
              }`}
              data-testid={`po-track-${req.ref_code}-${t.replace(/\s+/g, '-')}`}
            >
              <span>{t}</span>
              {done ? <CheckCircle2 size={16} className="text-emerald-600" /> :
                canClick ? <PlusCircle size={14} className="text-[#b48a45]" /> :
                       <span className="text-[10px] italic">not you</span>}
            </button>
          );
        })}
      </div>

      {finals.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {finals.map((f) => (
            <button
              key={f}
              disabled={!canFinalize}
              onClick={() => onOpenModal(f)}
              className={`btn-vgws bg-[#2d6b6b] border-[#1c4a4a] hover:bg-[#1c4a4a] ${!canFinalize ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid={`po-finalize-${req.ref_code}-${f.replace(/\s+/g, '-')}`}
            >
              <Send size={14} /> {f}
            </button>
          ))}
          {!allDone && !superUser && (
            <div className="text-xs text-[#6e7770]">
              Finalise buttons unlock once every track is done.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------- ATTACHMENTS DROPDOWN ------------------- */
function FulfilmentAttachments({ req, expanded, onToggle }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const history = req.fulfilment_history || [];
  const files = history.flatMap((h) => (h.attachments || []).map((a) => ({ ...a, when: h.at, by: h.actor_name, sub: h.sub_status })));
  if (!expanded && files.length === 0) return null;

  return (
    <div className="mt-3 border-t border-[#f2ecdf] pt-3" data-testid={`po-attachments-${req.ref_code}`}>
      <button onClick={onToggle} className="text-xs text-[#4a524c] inline-flex items-center gap-1 hover:text-[#7a1f2b]">
        <Paperclip size={12} /> Bills & documents ({files.length})
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {expanded && (
        <ul className="mt-2 space-y-1 text-xs">
          {files.length === 0 && <li className="text-[#6e7770]">No documents uploaded yet.</li>}
          {files.map((f, i) => (
            <li key={i} className="flex items-center gap-2">
              <a href={`${BACKEND}${f.url}`} target="_blank" rel="noreferrer" className="link-vgws truncate">{f.filename}</a>
              <span className="text-[#6e7770]">· {f.sub} · {f.by} · {new Date(f.when).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------- UPDATE STATUS MODAL ------------------- */
function UpdateStatusModal({ open, req, initialSub, cfg, user, onClose, onDone }) {
  const [sub, setSub] = useState("");
  const [remarks, setRemarks] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [tatKey, setTatKey] = useState("none");
  const [overrideAck, setOverrideAck] = useState(false);
  const remRef = useRef(null);

  const superUser = isSuperAdmin(user);
  const iAmOwner = req ? (req.process_owner_ids || []).includes(user.id) : false;
  const isOverride = superUser && !iAmOwner;

  useEffect(() => {
    if (!open) return;
    setSub(initialSub || "");
    setRemarks(""); setAttachment(null); setError(""); setTouched(false);
    setTatKey("none"); setOverrideAck(false);
    setTimeout(() => remRef.current?.focus(), 30);
  }, [open, initialSub, req]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); }
      else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, remarks, sub, attachment, tatKey, overrideAck]);

  if (!open || !req || !cfg) return null;

  const trimmed = remarks.trim();
  const minOk = trimmed.length >= 10;
  const showErr = touched && !minOk;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File larger than 10 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment({ field_name: `fulfilment_${sub}`, filename: data.filename, url: data.url, size: data.size });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!sub) { setError("Choose a sub-status."); return; }
    if (!minOk) { setTouched(true); return; }
    if (isOverride && !overrideAck) { setOverrideAck(true); return; }
    setBusy(true); setError("");
    try {
      const payload = { sub_status: sub, remarks: trimmed, attachments: attachment ? [attachment] : [] };
      const hours = tatHoursFor(tatKey);
      if (hours) payload.manual_tat_hours = hours;
      const { data } = await api.post(`/requisitions/${req.ref_code}/update-status`, payload);
      toast.success(`${sub} — ${req.ref_code}`, { duration: 4000 });
      onDone?.(data);
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally { setBusy(false); }
  };

  // Two-step override confirmation
  if (isOverride && !overrideAck) {
    const assignedTo = `${(req.process_owner_ids || []).length} process owner${(req.process_owner_ids || []).length !== 1 ? "s" : ""}`;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
           data-testid="po-override-modal" onClick={onClose}>
        <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
             onClick={(e) => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-[#e2d7c3] bg-[#faf5eb] flex items-center gap-2">
            <ShieldAlert className="text-[#b3261e]" size={20} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">Super Admin override</h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            <p className="text-sm text-[#1f2320]">
              You are acting as <strong>Super Admin</strong> on behalf of{" "}
              <strong>{assignedTo}</strong>. This action will be recorded in the audit trail
              with <span className="font-mono text-xs bg-[#faf5eb] px-1.5 py-0.5 rounded">acted_on_behalf_of</span>.
              Continue?
            </p>
          </div>
          <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
            <button onClick={onClose} className="btn-ghost" data-testid="po-override-cancel">Cancel</button>
            <button onClick={() => setOverrideAck(true)} className="btn-vgws" data-testid="po-override-confirm">
              Continue as Super Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         data-testid="update-status-modal" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Send size={18} className="text-[#b48a45]" />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Update status · {req.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f9f4e8]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {isOverride && (
            <div className="text-xs text-white bg-[#7a1f2b] px-3 py-2 rounded flex items-start gap-2"
                 data-testid="po-super-admin-banner">
              <ShieldAlert size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong>Super Admin override.</strong> Acting on behalf of the assigned
                process owners. This will be logged in the audit trail.
              </div>
            </div>
          )}
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded" data-testid="us-error">{error}</div>
          )}
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">New sub-status</span>
            <input readOnly value={sub} className="input-vgws bg-[#faf5eb] font-medium" data-testid="us-substatus" />
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks <span className="text-[#b3261e]">*</span>
              <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">min 10 · Ctrl+Enter to confirm</span>
            </span>
            <textarea
              ref={remRef}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
              onBlur={() => setTouched(true)}
              rows={4}
              placeholder="e.g. Quote received from Amit Enterprises, ₹18,400 incl. GST. Awaiting Bursar sign-off."
              className={`input-vgws min-h-[110px] ${showErr ? "!border-[#b3261e] ring-1 ring-[#b3261e]/30" : ""}`}
              data-testid="us-remarks"
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={minOk ? "text-[#2d6b6b]" : "text-[#b3261e]"} data-testid="us-remarks-error">
                {minOk ? "Looks good." : "Remark description is mandatory (minimum 10 characters)."}
              </span>
              <span className={`font-mono ${minOk ? "text-[#2d6b6b]" : "text-[#6e7770]"}`}>{trimmed.length} / 1000</span>
            </div>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5 inline-flex items-center gap-1">
              <Timer size={14} className="text-[#2d6b6b]" />
              Set follow-up TAT
              <span className="ml-1 text-[10px] uppercase tracking-widest text-[#6e7770]">optional · working hours</span>
            </span>
            <select value={tatKey} onChange={(e) => setTatKey(e.target.value)}
                    className="input-vgws" data-testid="us-manual-tat">
              {MANUAL_TAT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
            <div className="mt-1 text-[11px] text-[#6e7770]">
              A single reminder fires when the deadline is reached. No cascade, no escalation.
            </div>
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Attach bill / PO / invoice <span className="text-[10px] uppercase tracking-widest text-[#6e7770] ml-1">optional · 10 MB · PDF / image</span>
            </span>
            <div className="flex items-center gap-2">
              <label className="btn-ghost cursor-pointer" data-testid="us-upload-label">
                <Paperclip size={14} /> {attachment ? "Replace file" : "Choose file"}
                <input type="file" className="hidden" accept="application/pdf,image/*"
                       onChange={onFile} disabled={uploading || busy} data-testid="us-upload" />
              </label>
              {uploading && <span className="text-xs text-[#6e7770] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading…</span>}
              {attachment && !uploading && (
                <span className="text-xs text-[#2d6b6b]" data-testid="us-attachment-name">✔ {attachment.filename}</span>
              )}
            </div>
          </label>
        </div>

        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost" data-testid="us-cancel">Cancel</button>
          <button onClick={submit} disabled={!minOk || busy || uploading}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f] ${(!minOk || busy || uploading) ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="us-confirm">
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send size={14} />}
            {busy ? "Saving…" : "Confirm update"}
          </button>
        </div>
      </div>
    </div>
  );
}
